import { useSelector } from "react-redux";
import { useEffect, useRef, useState } from "react";
import { toast } from "react-toastify";
import RemoveCircleIcon from "@mui/icons-material/RemoveCircleOutline";
import {
  Box,
  Button,
  FormControlLabel,
  Grid,
  IconButton,
  InputAdornment,
  MenuItem,
  Radio,
  RadioGroup,
  Switch,
  Typography,
} from "@mui/material";
import {
  Field,
  FieldArray,
  FieldArrayRenderProps,
  FormikTouched,
} from "formik";
import CheckIcon from "@mui/icons-material/Check";
import ClearIcon from "@mui/icons-material/Clear";
import FormikTextField from "../../../../components/common/formikFields/FormikTextField";
import {
  ClientInvoiceFuelSurchargeFields,
  EditClientInvoiceRouteRateUIRequest,
  EditClientInvoiceUIRequest,
  FuelSurchargeDateRangeValue,
} from "../ClientInvoiceInterfaces";
import {
  RootState,
  useAppDispatch,
} from "../../../../app/features/store/store";
import { fetchRegionNameListForFuelSurcharge } from "../../../../app/features/ratecard/ratecardSlice";
import getAxiosError from "../../../../app/api/axiosError";
import Loader from "../../../../components/common/loader/Loader";
import { FUEL_SURCHARGE_FIELD_TYPES } from "../../ratecard/RateCardConstants";
import { mapSnakeCaseToCapitalCaseWithSpaces } from "../../../../components/common/utils/StringUtils";
import { prepareAndDownloadFile } from "../../../helper/helper";
import { useSubmitModal } from "../../../../components/common/modalPopup/useSubmitModal";
import FileUploadModal from "../../../../components/common/modalPopup/FileUploadModal";
import DatePicker from "react-multi-date-picker";
import {
  isDisabledDate,
  isDisabledFieldDueToDateRangeEdits,
} from "../utils/RatecardUtils";
import AddIcon from "@mui/icons-material/Add";
import { formatDate } from "../../../../components/common/utils/DateUtils";
import DeleteIcon from "@mui/icons-material/Delete";
import {
  ClientInvoiceEditDetailResponse,
  RouteResponse,
} from "../../../../app/models/responseModels/ClientInvoiceResponses";
import {
  RatecardResponse
} from "../../ratecard/dtos/RateCardResponse";
import InfoWithTooltip from "../InfoWithToolTip";
import { setEmitFlags } from "typescript";

interface Props {
  values: EditClientInvoiceRouteRateUIRequest;
  touched: FormikTouched<EditClientInvoiceUIRequest>;
  handleChange: any;
  handleBlur: any;
  setFieldValue: any;
  invoiceStartDate: Date;
  invoiceEndDate: Date;
  route: RouteResponse | null;
  originalRatecardData: RatecardResponse | undefined;
  store: string;
  editData: ClientInvoiceEditDetailResponse | null;
}

const CiFuelSurcharge = (props: Props) => {
  const { values, handleChange, handleBlur, setFieldValue } = props;

  const ArrayHelperRef = useRef<FieldArrayRenderProps>();

  // import store data from "Redux-Store"
  const { isLoading, regionNameList } = useSelector(
    (state: RootState) => state.ratecard
  );
  const { storesForCurrentClientInvoice } = useSelector(
    (state: RootState) => state.clientInvoice
  );
  const dispatch = useAppDispatch();

  useEffect(() => {
    if (values.show_fuel_surcharge && values.fuelType) {
      fetchRegionNameListForFuelSurchargeData();

      if (props.touched.fuelType) {
        // this check is here else regionName will be changed to undefined on the first render as well
        // so the touched map from formik tells us if fuelType has been deliberately changed after the intial render is done
        setFieldValue("regionName", undefined);
      }
    }
  }, [values.show_fuel_surcharge, values.fuelType]);

  const [tooltipText, setTooltipText] = useState<string>("");

  useEffect(() => {
    // Extract user-selected dates from master date range or fuel surcharge date range
    const userStartDate = new Date(
      values?.master_date_range?.length
        ? values.master_date_range[0]
        : values?.fuel_surcharge_date_range[0]?.toDate()
    );
    const userEndDate = new Date(
      values?.master_date_range?.length
        ? values.master_date_range[1]
        : values?.fuel_surcharge_date_range[1]?.toDate()
    );

    // Function to check if user-selected dates collide with an existing date range
    const isDateRangeColliding = (
      existingStart: Date,
      existingEnd: Date
    ): boolean => {
      return userStartDate <= existingEnd && userEndDate >= existingStart;
    };

    // Identify conflicting stores based on date collisions with all existing ranges
    const conflictingStoreNames = (
      values?.fuel_surcharge_date_range_values || []
    ).reduce((acc, existingRange) => {
      const existingStartDate = new Date(existingRange.startDate);
      const existingEndDate = new Date(existingRange.endDate);

      if (isDateRangeColliding(existingStartDate, existingEndDate)) {
        const existingStoreIds = existingRange?.storeIds;
        const conflictingStores = props.store
          ?.split(",")
          .map(Number)
          .filter((storeId) => existingStoreIds?.includes(storeId))
          .map(
            (storeId) =>
              storesForCurrentClientInvoice.find(
                (store) => store.id === storeId
              )?.storeId
          )
          .filter(Boolean) as string[];

        acc.push(...conflictingStores); // Accumulate conflicting store names
      }

      return acc;
    }, [] as string[]);

    // Set tooltip text with warning message if conflicts exist
    const warnings =
      conflictingStoreNames.length > 0
        ? `This date range edit will not work for the stores mentioned below as they have conflicting date ranges:\n\n${conflictingStoreNames
            .map((storeName) => `- ${storeName}`)
            .join("\n")}`
        : "";

    setTooltipText(warnings);
  }, [values?.fuel_surcharge_date_range, values?.master_date_range]);

  const fetchRegionNameListForFuelSurchargeData = async () => {
    try {
      const response = await dispatch(
        fetchRegionNameListForFuelSurcharge({
          fuelType: values.fuelType ?? "petrol",
        })
      ).unwrap();
      if (!response.isSuccess) {
        toast.error(response.message, {
          toastId: "fetchRegionNameListForFuelSurcharge",
        });
        return;
      }
    } catch (error) {
      const appError = getAxiosError(error);
      toast.error(appError.errorMessage, {
        toastId: "fetchRegionNameListForFuelSurcharge",
      });
    } finally {
      // setDataFetchig(false);
    }
  };

  const isDisabledField = (
    fuelSurchargeFields: ClientInvoiceFuelSurchargeFields[],
    fieldToCheck: string
  ) => {
    let fieldsStringList: string[] = [];
    fuelSurchargeFields.forEach((field) =>
      fieldsStringList.push(field.fieldName)
    );

    return fieldsStringList.includes(fieldToCheck);
  };

  const handleFSCFileData = async (
    fileMappedData: any[],
    invalidData: any[]
  ) => {
    // flag file upload process as true
    // used for validation
    values.lookup_file_upload = true;
    const errorFileName = "Error_FSC_Table_";

    try {
      const invalidFileData = invalidData.map(({ data, error }) => ({
        AtLeast: data.rate_range_start,
        "But Less Than": data.rate_range_end,
        Surcharge: data.fuel_surcharge_percentage,
        Error: error,
      }));
      // if (fileMappedData.length === 0) {
      if (invalidFileData.length > 0) {
        // reset the lookup data
        // for validation
        values.fuelSurchargeLookup = [];
        prepareAndDownloadFile(invalidFileData, errorFileName);
        return false;
      } else {
        values.fuelSurchargeLookup = fileMappedData;
      }
    } catch (error) {
      const appError = getAxiosError(error);
      toast.error(appError.errorMessage, {
        toastId: "FSCTableException",
      });
    }
    return true;
  };

  // open  modal logic  incase user selects any of the store
  const [fileUploadModal, openFileUploadModal] = useSubmitModal(
    (closeFileUploadModal) => (
      <FileUploadModal
        closeFileUploadModal={closeFileUploadModal}
        onCompleted={() => {
          // implement file upload modal onCompleted
        }}
        expectedColumns={[
          {
            name: "At Least",
            mandatory: true,
            type: "number",
            camelCaseName: "rateRangeStart",
            regex: /^\d*\.?\d*$/,
          },
          {
            name: "But Less Than",
            mandatory: true,
            type: "number",
            camelCaseName: "rateRangeEnd",
            regex: /^\d*\.?\d*$/,
          },
          {
            name: "Surcharge",
            mandatory: true,
            type: "percentage",
            camelCaseName: "fuelSurchargePercentage",
            regex: /^\d*\.?\d*$/,
          },
        ]}
        asyncSubmitOperation={handleFSCFileData}
        sampleFileName={"FSC-Table-Sample.xlsx"}
        acceptNumberAsZero={true}
        acceptPercentageAsZero={true}
      />
    )
  );

  const disableFieldDateRanges = () => {
    if (
      props.values.master_date_range === undefined ||
      (props.values.master_date_range &&
        props.values.master_date_range.length < 2)
    )
      return false;
    return true;
  };

  const resetFuelSurchargeFieldEditsAndDateRange = (
    dateRangeFieldName: string
  ) => {
    setFieldValue(`fuel_surcharge_custom_value`, undefined);
    setFieldValue(`fuelType`, undefined);
    setFieldValue(`regionName`, undefined);
    setFieldValue(`fuelSurchargeFields`, []);
    setFieldValue(`fuelSurchargeLookup`, []);
    setFieldValue(`show_fuel_surcharge`, false);

    setFieldValue(dateRangeFieldName, []);
  };

  const getUsedDateRanges = (
    dateRangesValues: FuelSurchargeDateRangeValue[]
  ) => {
    return dateRangesValues.map((item) => {
      // item.date_range.map((dateObject) => dateObject.toDate())

      let startDate = item.startDate;
      let endDate = item.endDate;
      // this if is used for special case where we fetch date range edits and get them as string
      // as that created a type issue so I converted that to Date object right here
      if (typeof startDate === "string") startDate = new Date(startDate);
      if (typeof endDate === "string") endDate = new Date(endDate);
      return [startDate, endDate];
    });
  };
  const isMultiStoreEdit = (store: string): boolean => {
    return store?.split(",").map(Number).length > 1;
  };
  return (
    <>
<Grid container alignItems={"center"}>
  <Loader isLoading={isLoading} />
  <Grid item xs={12} sm={6} display="flex" alignItems="center">
    <FormControlLabel
      id="show_fuel_surcharge"
      name="show_fuel_surcharge"
      label="Fuel Surcharge"
      labelPlacement="start"
      disabled={
        values.master_date_range && values.master_date_range.length === 2
          ? isDisabledFieldDueToDateRangeEdits(
              values.master_date_range[0],
              values.master_date_range[1],
              getUsedDateRanges(values.fuel_surcharge_date_range_values)
            )
          : false
      }                                                                             
      sx={{ mt: 2, mr: 2 }}
      checked={values.show_fuel_surcharge}
      control={
        <Field
          id="show_fuel_surcharge"
          name="show_fuel_surcharge"
          component={Switch}
          type="checkbox"
          size="small"
          onChange={handleChange}
          onBlur={handleBlur}
        />
      }
    />
    {values.show_fuel_surcharge && (
      <div style={{ display: "flex", alignItems: "center", marginLeft: "16px" }}>
        <DatePicker
          format="MM/DD/YYYY"
          className="green"
          dateSeparator=" ~ "
          range
          rangeHover
          placeholder="Date Range"
          style={{ height: "36px", marginTop: "8px" }}
          minDate={props.invoiceStartDate}
          maxDate={(() => {
            const adjustedEndDate = new Date(props.invoiceEndDate);
            adjustedEndDate.setHours(23, 59, 59, 999);
            return adjustedEndDate;
          })()}
          disabled={disableFieldDateRanges()}
          value={values.fuel_surcharge_date_range}
          onChange={(value) => {
            setFieldValue(`fuel_surcharge_date_range`, value);
          }}
          mapDays={({ date }) => {
            let localProps: any = {};
            if (!isMultiStoreEdit(props?.store)) {
              const usedDateRanges = values.fuel_surcharge_date_range_values.map(
                (item) => {
                  let startDate =
                    typeof item.startDate === "string"
                      ? new Date(item.startDate)
                      : item.startDate;
                  let endDate =
                    typeof item.endDate === "string"
                      ? new Date(item.endDate)
                      : item.endDate;
                  return [startDate, endDate];
                }
              );

              if (isDisabledDate(date.toDate(), usedDateRanges)) {
                localProps.disabled = true;
                localProps.style = { color: "gray", pointerEvents: "none" };
              }
            }

            return localProps;
          }}
        />
        <IconButton
          color="success"
          style={{ marginLeft: "8px" }} // Added slight space
          disabled={
            disableFieldDateRanges() ||
            (values.fuel_surcharge_date_range &&
              values.fuel_surcharge_date_range.length < 2)
          }
          onClick={() => {
            if (values.fuel_surcharge_date_range.length === 2) {
              let dateRangeValues = values.fuel_surcharge_date_range_values;

              let fsDateRangeValue: FuelSurchargeDateRangeValue = {
                startDate: values.fuel_surcharge_date_range[0].toDate(),
                endDate: values.fuel_surcharge_date_range[1].toDate(),
              };

              if (values.fuel_surcharge_custom_value)
                fsDateRangeValue.fuelSurchargeCustomValue =
                  values.fuel_surcharge_custom_value;
              else {
                fsDateRangeValue.fuelType = values.fuelType;
                fsDateRangeValue.regionName = values.regionName;
                fsDateRangeValue.fuelSurchargeFields =
                  values.fuelSurchargeFields;
                fsDateRangeValue.fuelSurchargeLookup =
                  values.fuelSurchargeLookup;
              }

              dateRangeValues.push(fsDateRangeValue);
              setFieldValue(
                `fuel_surcharge_date_range_values`,
                dateRangeValues
              );

              resetFuelSurchargeFieldEditsAndDateRange(
                `fuel_surcharge_date_range`
              );
            }
          }}
        >
          <AddIcon />
        </IconButton>
      </div>
    )}
  </Grid>
  <Grid item xs={1}>
    <InfoWithTooltip
      tooltipText={tooltipText}
      isDisabled={tooltipText === "" ? true : false}
      sx={{ color: "orange" }}
    />
  </Grid>
</Grid>

<Grid
  container
  item
  xs={12}
  sm={12}
  columnSpacing={2}
  alignItems={"center"}
  display={values.show_fuel_surcharge ? "" : "none"}
>
  <Grid item sm={3} paddingTop={1}>
    <FormikTextField
      id="fuel_surcharge_custom_value"
      name="fuel_surcharge_custom_value"
      variant="outlined"
      size="small"
      label="Add Custom Value"
      type="number"
      InputProps={{
        startAdornment: (
          <InputAdornment position="start">$</InputAdornment>
        ),
      }}
    />
  </Grid>

  {/* New Row */}
  <Grid container item xs={12} spacing={2} alignItems="center">
    <Grid item xs={12} sm={3} paddingTop={1}>
      <RadioGroup
        row
        id="fuelType"
        name="fuelType"
        value={values.fuelType}
        onChange={handleChange}
        onBlur={handleBlur}
      >
        <FormControlLabel
          value="petrol"
          disabled={values.fuel_surcharge_custom_value ? true : false}
          control={<Radio />}
          label="Petrol"
        />
        <FormControlLabel
          value="diesel"
          disabled={values.fuel_surcharge_custom_value ? true : false}
          control={<Radio />}
          label="Diesel"
        />
      </RadioGroup>
    </Grid>
    <Grid item xs={12} sm={3}>
      <FormikTextField
        select
        margin="normal"
        id="regionName"
        name="regionName"
        label="Choose city/state"
        size="small"
        fullWidth
        variant="outlined"
        isDisabled={
          values.fuel_surcharge_custom_value
            ? true
            : !values.fuelType || values.fuelType === ""
            ? true
            : false
        }
      >
        {regionNameList.map((region, index) => (
          <MenuItem key={index} value={region.toLowerCase()}>
            {region}
          </MenuItem>
        ))}
      </FormikTextField>
    </Grid>
    <Grid item xs={12} sm={3} paddingTop={1}>
      <Button
        id="fscFile"
        name="fscFile"
        variant="contained"
        size="medium"
        disabled={
          values.fuel_surcharge_custom_value
            ? true
            : !values.fuelType ||
              values.fuelType === "" ||
              !values.regionName ||
              values.regionName === ""
            ? true
            : false
        }
        onClick={openFileUploadModal}
        color={
          values.fuelSurchargeLookup.length === 0 ? "error" : "success"
        }
        endIcon={
          values.fuelSurchargeLookup.length === 0 ? (
            <ClearIcon />
          ) : (
            <CheckIcon />
          )
        }
      >
        Upload FSC table
      </Button>
    </Grid>
  </Grid>
</Grid>

      {/* Fuel Surcharge Fields */}
      <Grid
        container
        item
        alignItems={"center"}
        columnSpacing={2}
        display={values.show_fuel_surcharge ? "" : "none"}
      >
        <FieldArray
          name="fuelSurchargeFields"
          render={(arrayHelpers) => {
            ArrayHelperRef.current = arrayHelpers;

            return values.fuelSurchargeFields.map((field, index) => (
              <>
                <Grid item sm={1} display={index === 0 ? "none" : ""}>
                  <FormikTextField
                    select
                    margin="normal"
                    id={`fuelSurchargeFields.${index}.fieldOperator`}
                    name={`fuelSurchargeFields.${index}.fieldOperator`}
                    label="Plus/Minus"
                    size="small"
                    fullWidth
                    isDisabled={
                      values.fuel_surcharge_custom_value ? true : false
                    }
                    variant="outlined"
                  >
                    {["+", "-"].map((fieldOperator, index) => (
                      <MenuItem key={index} value={fieldOperator}>
                        {fieldOperator}
                      </MenuItem>
                    ))}
                  </FormikTextField>
                </Grid>
                <Grid item sm={index === 0 ? 3 : 2}>
                  <FormikTextField
                    select
                    margin="normal"
                    id={`fuelSurchargeFields.${index}.fieldName`}
                    name={`fuelSurchargeFields.${index}.fieldName`}
                    label="Field Name"
                    size="small"
                    fullWidth
                    variant="outlined"
                    isDisabled={
                      values.fuel_surcharge_custom_value ? true : false
                    }
                  >
                    {FUEL_SURCHARGE_FIELD_TYPES.map((field, index) => (
                      <MenuItem
                        key={index}
                        value={field}
                        disabled={isDisabledField(
                          values.fuelSurchargeFields,
                          field
                        )}
                      >
                        {mapSnakeCaseToCapitalCaseWithSpaces(field)}
                      </MenuItem>
                    ))}
                  </FormikTextField>
                </Grid>
                <Grid item sm={1}>
                  <IconButton
                    color="error"
                    disabled={values.fuel_surcharge_custom_value ? true : false}
                    onClick={() => {
                      arrayHelpers.remove(index);
                    }}
                  >
                    <RemoveCircleIcon />
                  </IconButton>
                </Grid>
              </>
            ));
          }}
        />
      </Grid>
      <Grid container item display={values.show_fuel_surcharge ? "" : "none"}>
        <Button
          variant="contained"
          disabled={values.fuel_surcharge_custom_value ? true : false}
          onClick={() => {
            if (values.fuelSurchargeFields.length === 0)
              ArrayHelperRef.current?.push({
                fieldOperator: "+",
                fieldName: undefined,
              });
            else ArrayHelperRef.current?.push({});
          }}
        >
          Add Fields
        </Button>
      </Grid>
      {/* Date range edits for Fuel Surcharge */}
      {props.route === null ? (
        <Grid container item sm={12} md={12} lg={12}>
          {values.fuel_surcharge_date_range_values.map((item, index) => {
            return (
              <Box display="block" width="100%">
                <Typography variant="overline">
                  <>
                    Date Range: {formatDate(item.startDate)} to{" "}
                    {formatDate(item.endDate)}
                  </>
                </Typography>
                {item.fuelSurchargeCustomValue ? (
                  <Typography variant="overline">
                    <>, Custom Value: {item.fuelSurchargeCustomValue}</>
                  </Typography>
                ) : null}
                {!item.fuelSurchargeCustomValue ? (
                  <Typography variant="overline">
                    <>
                      , Fuel Type: {item.fuelType}, Region Name:{" "}
                      {item.regionName}, Fields:{" "}
                      {item.fuelSurchargeFields?.map((field, index) => {
                        let result = "";
                        if (index === 0) result = result + field.fieldName;
                        else
                          result =
                            result +
                            " " +
                            field.fieldOperator +
                            " " +
                            field.fieldName;
                        return result;
                      })}
                    </>
                  </Typography>
                ) : null}
                <IconButton
                  color="error"
                  size="small"
                  onClick={() => {
                    const dateRangeValues =
                      values.fuel_surcharge_date_range_values.filter(
                        (item, idx) => idx !== index
                      );

                    setFieldValue(
                      `fuel_surcharge_date_range_values`,
                      dateRangeValues
                    );
                    setFieldValue('fuel_surcharge_custom_value', "")
                  }}
                >
                  <DeleteIcon />
                </IconButton>
              </Box>
            );
          })}
        </Grid>
      ) : null}
      {/* Original Fuel Surcharge */}
      {props.route !== null &&
      props.editData?.ratecardFuelSurchargeFields &&
      props.editData.ratecardFuelSurchargeFields.length > 0 ? (
        <Grid
          id="original-fuel-surcharge"
          container
          item
          sm={12}
          md={12}
          lg={12}
        >
          <Box display="block" width="100%">
            <Typography variant="overline" color={"rgb(0, 0, 0, 0.6);"}>
              <>
                Original:- Fuel Type:{" "}
                {props.originalRatecardData?.ratecardValues.fuelType}, Region
                Name: {props.originalRatecardData?.ratecardValues.regionName},
                {props.originalRatecardData?.ratecardFuelSurchargeFields.map(
                  (field, index) => {
                    // not adding custom values as custom value is not there in the original ratecard data
                    let result = "";
                    if (index === 0) result = result + field.fieldName;
                    else
                      result =
                        result +
                        " " +
                        field.fieldOperator +
                        " " +
                        field.fieldName;
                    return result;
                  }
                )}
              </>
            </Typography>
          </Box>
        </Grid>
      ) : null}
      {fileUploadModal}
    </>
  );
};

export default CiFuelSurcharge;
